import React, { useCallback, useRef, useState } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import AlertManager from "../../shared/Alert/Alert";
import { downloadTemplateFile } from "../../API/landedCostVendorDownloadTemplateFileApi";
import { uploadLandedCostCalculationFile } from "../../API/landedCostCalculationUploadApi";
import {
  ACCEPTED_MIME_TYPES,
  FILE_ACCEPTED_TYPES,
  MAX_FILE_SIZE_MB,
} from "../LandedCostUpload/LandedCostUploadConstants";
import layoutClasses from "../LandedCostUpload/LandedCostUpload.module.css";
import pageClasses from "./LandedCostCalculation.module.css";

const UPLOAD_TIMEOUT_MS = 2000;

const LandedCostCalculation = () => {
  const fileInputRef = useRef(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (event) => {
    const file = event?.target?.files?.[0];
    setSelectedFileName(file ? file.name : "");
  };

  const clearSelectedFile = useCallback(() => {
    setSelectedFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleTemplateDownload = async () => {
    try {
      await downloadTemplateFile("DATAVIEW");
    } catch (error) {
      AlertManager.error("Unable to download the landed cost calculation template.", {
        contactSupport: false,
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const file = fileInputRef.current?.files?.[0];

    if (!file) {
      AlertManager.error("Choose a spreadsheet to upload.", { contactSupport: false });
      clearSelectedFile();
      return;
    }
    const isExcel = ACCEPTED_MIME_TYPES.includes(file.type) || /\.xls[x]?$/i.test(file.name);
    if (!isExcel) {
      AlertManager.error("Only .xlsx or .xls files are supported.", {
        contactSupport: false,
      });
      clearSelectedFile();
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      AlertManager.error(`File size exceeds ${MAX_FILE_SIZE_MB} MB.`, {
        contactSupport: false,
      });
      clearSelectedFile();
      return;
    }

    setUploading(true);
    try {
      const uploadPromise = uploadLandedCostCalculationFile({ file });
      const timeoutPromise = new Promise((resolve) =>
        setTimeout(() => resolve({ timedOut: true }), UPLOAD_TIMEOUT_MS)
      );
      const result = await Promise.race([
        uploadPromise.then((res) => ({ res })),
        timeoutPromise,
      ]);

      if (result?.timedOut) {
        AlertManager.success(
          "File received. Processing has started and you will receive an email when it is complete.",
          { contactSupport: false }
        );
        // Ensure any late rejections from the upload promise are handled.
        uploadPromise.catch(() => {});
        event.target.reset();
        clearSelectedFile();
        return;
      }

      const response = result?.res;
      if (!response) {
        clearSelectedFile();
        return;
      }

      if (response.ok || response.status === 200 || response.status === 201) {
        AlertManager.success(
          "File received. Processing has started and you will receive an email when it is complete. Thank you.",
          { contactSupport: false }
        );
        event.target.reset();
        clearSelectedFile();
        return;
      }

      clearSelectedFile();
      return;
    } catch (error) {
      clearSelectedFile();
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className={`${layoutClasses.body} ${pageClasses.page}`}>
      <header className={pageClasses.header}>
        <div>
          <h1 className={pageClasses.title}>Landed cost calculation</h1>
          <p className={pageClasses.subtitle}>
            Please upload the Napa skus (parsed SKU number) and Hq line for re-landing
          </p>
        </div>
      </header>

      <form className={layoutClasses.container} onSubmit={handleSubmit}>
        <Box className={layoutClasses.fieldRow}>
          <Button component="label" variant="outlined" size="small" disabled={uploading}>
            Choose File
            <input
              hidden
              ref={fileInputRef}
              type="file"
              accept={FILE_ACCEPTED_TYPES}
              onChange={handleFileChange}
              data-testid="landed-cost-calculation-file-input"
            />
          </Button>
          <Typography variant="body2" className={layoutClasses.fileNameDisplay}>
            {selectedFileName || "No file selected"}
          </Typography>
          <Typography variant="body2" className={layoutClasses.helperText}>
            Accepted formats: .xlsx / .xls Max size {MAX_FILE_SIZE_MB} MB
          </Typography>
        </Box>

        <Box className={layoutClasses.actions}>
          <Button
            className={layoutClasses.uploadBtn}
            type="submit"
            disabled={uploading}
            data-testid="landed-cost-calculation-upload-btn"
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
          <Tooltip title="Download the landed cost calculation template">
            <span>
              <Button
                className={layoutClasses.uploadBtn}
                variant="text"
                onClick={handleTemplateDownload}
                disabled={uploading}
                data-testid="landed-cost-calculation-template-download"
              >
                Download Template
              </Button>
            </span>
          </Tooltip>
        </Box>
      </form>

      {/*<div className={layoutClasses.hLine} />

       <div className={layoutClasses.fileuploadHistory}>
        <RunDetailsTable
          runDetails={workflowRows}
          loading={workflowLoading}
          variant="detail"
          showUploadButton={false}
          showActions={false}
        />
      </div> */}
    </section>
  );
};

export default LandedCostCalculation;
